import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowRightLeft, BadgeCheck, Calculator, Gavel, History as HistoryIcon, MapPin, MessageSquareWarning, Phone, Send, TimerReset, Undo2, UserRound, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Skeleton } from "../ui/skeleton";
import { Textarea } from "../ui/textarea";
import { api, errMsg, fmtDateTime, fmtINR } from "../../lib/api";
import { useMasterOptions } from "../../pages/collector/FieldForms";
import { SafFloorEditor, floorsToEditor, floorsPayload } from "./SafFloorEditor";
import { SafTaxEstimate, SafTaxTable } from "./SafTaxEstimate";
import { SafDemandLinesEditor, linesToEditor, linesPayload, linesTotal } from "./SafDemandLinesEditor";
import { SafEscalationTags, SafStatusBadge, SafTimeline, stageDeadlineText } from "./SafStatus";

/**
 * Phase 207 / 210 — one SAF case, for every staff role:
 *  - Field Collector (assigned, status pending / sent back): verify details + floors, enter FINAL demand lines, submit.
 *  - Supervisor (status collector_verified, own territory): modify lines, approve or send back.
 *  - Municipal Admin / Super Admin (status supervisor_approved / collector_verified): modify lines, final approve (PIN), send back, reject;
 *    assign / reassign the collector while the case is open. Phase 210: they may also decide DIRECTLY at any open stage
 *    (even while the case is with the Collector / Supervisor) — a reason is mandatory and is shown to the citizen.
 * Everyone sees the citizen's concern, the citizen's estimate, routing / escalation tags, the stage clock and the full timeline.
 */
const Field = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <p className="text-[11px] text-muted-foreground">{label}</p>
    <p className="text-sm font-medium break-words">{value || "—"}</p>
  </div>
);

export function AssignCollectorDialog({ code, saf, open, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !saf) return;
    setSel(""); setRemarks(""); setData(null);
    api.get(`/t/${code}/saf/${saf.id}/collectors`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [open, saf, code]);
  const submit = async () => {
    if (!sel) return toast.error("Pick a collector");
    setBusy(true);
    try {
      await api.put(`/t/${code}/saf/${saf.id}/assign`, { collector_id: sel, remarks: remarks.trim() || null });
      toast.success("Field Collector assigned — they have been notified");
      onDone?.();
      onClose?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="saf-assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base">{saf?.assigned_collector_id ? "Reassign" : "Assign"} Field Collector — {saf?.saf_id}</DialogTitle>
          <DialogDescription>Collectors mapped to {data?.ward_name || "this ward"} are listed first. The collector is notified immediately.</DialogDescription>
        </DialogHeader>
        {!data ? <Skeleton className="h-10 w-full" /> : (
          <div className="space-y-3">
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger data-testid="saf-assign-select" aria-label="Collector"><SelectValue placeholder="Select collector" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {data.collectors.map((c) => (
                  <SelectItem key={c.id} value={c.id} disabled={c.is_current} data-testid={`saf-assign-option-${c.id}`}>
                    {c.name}{c.employee_id ? ` (${c.employee_id})` : ""}{c.covers_ward ? " · covers ward" : ""}{c.is_current ? " · current" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea rows={2} placeholder="Reason / note (optional)" value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="saf-assign-remarks" />
            <Button className="w-full" onClick={submit} disabled={busy || !sel} data-testid="saf-assign-submit">
              <ArrowRightLeft className="h-4 w-4 mr-2" /> {busy ? "Assigning…" : "Assign collector"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function SafCasePanel({ code, safId, role, onDone, testIdPrefix = "saf-case" }) {
  const masters = useMasterOptions(code);
  const [saf, setSaf] = useState(null);
  const [error, setError] = useState(null);
  const [taxHeads, setTaxHeads] = useState([]);
  const [fys, setFys] = useState([]);
  const [fields, setFields] = useState({});
  const [floors, setFloors] = useState([]);
  const [lines, setLines] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [recalc, setRecalc] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);   // {action}
  const [direct, setDirect] = useState(null);     // Phase 210: {action: approve|reject}
  const [directReason, setDirectReason] = useState("");

  const load = useCallback(() => {
    setError(null);
    api.get(`/t/${code}/saf/${safId}`).then((r) => {
      const s = r.data;
      setSaf(s);
      setFields({ owner_name: s.owner_name || "", father_spouse_name: s.father_spouse_name || "", mobile: s.mobile || "", house_no: s.house_no || "",
        address: s.address || "", road_width_id: s.road_width_id || "", property_type_id: s.property_type_id || "" });
      setFloors(floorsToEditor(s.verified_floors || s.floors || []));
      const base = (s.demand_lines && s.demand_lines.length) ? s.demand_lines : (s.estimate?.lines || []);
      setLines(linesToEditor(base));
      setRemarks("");
    }).catch((e) => setError(errMsg(e)));
  }, [code, safId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get(`/t/${code}/masters/tax-heads`).then((r) => setTaxHeads((r.data || []).filter((h) => h.is_active !== false))).catch(() => {});
    api.get(`/t/${code}/masters/financial-years`).then((r) => setFys(r.data || [])).catch(() => {});
  }, [code]);

  const can = saf?.can_act || {};
  const isCollector = role === "tax_collector";
  const isAdmin = role === "municipal_admin" || role === "super_admin";   // Phase 210: Super Admin acts like the Municipal Admin
  const editFloors = isCollector && can.verify;
  // Phase 210: "direct only" = Admin / Super Admin looking at a case that is still with the Field Collector — the normal
  // final-review buttons do not apply (the API rejects them); the only staff actions are assign / reassign and a direct decision.
  const directOnly = isAdmin && !!can.direct_review && !can.admin_review;
  const editLines = (isCollector && can.verify) || (role === "supervisor" && can.supervisor_review) || (isAdmin && (can.admin_review || can.direct_review));
  const defaultFy = saf?.estimate?.financial_year_id || fys.find((f) => f.is_current)?.id || "";
  const shownFloors = useMemo(() => saf?.verified_floors || saf?.floors || [], [saf]);

  const recalculate = async () => {
    setRecalc(true);
    try {
      // Phase 209: recalculate for the same financial year(s) the citizen selected
      const fyIds = saf?.financial_year_ids?.length ? saf.financial_year_ids : (saf?.estimate?.financial_year_ids || []);
      const r = await api.post(`/t/${code}/saf/estimate`, { road_width_id: fields.road_width_id || saf.road_width_id, floors: floorsPayload(floors), owner_name: fields.owner_name,
        financial_year_ids: fyIds });
      setLines(linesToEditor(r.data.lines || []));
      const labels = (r.data.fy_labels || [r.data.fy_label]).filter(Boolean);
      toast.success(`Recalculated from the rate engine — ${fmtINR(r.data.total_demand)} for FY ${labels.join(", ")}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setRecalc(false); }
  };

  const act = async (action) => {
    const payloadLines = linesPayload(lines, defaultFy);
    if (!isCollector && (action === "reject" || action === "send_back") && remarks.trim().length < 3) {
      return toast.error(action === "reject" ? "Please enter the reason for rejection — it is shown to the citizen" : "Please enter what must be corrected — it is shown to the Field Collector");
    }
    setBusy(true);
    try {
      if (isCollector) {
        if (payloadLines.length === 0) throw new Error("Enter at least one final demand line");
        await api.put(`/t/${code}/saf/${saf.id}/verify`, { ...fields, road_width_id: fields.road_width_id || null, property_type_id: fields.property_type_id || null,
          floors: floorsPayload(floors), demand_lines: payloadLines, remarks: remarks.trim() || null });
        toast.success("Verification submitted — sent to the Supervisor for review");
      } else if (role === "supervisor") {
        await api.put(`/t/${code}/saf/${saf.id}/supervisor-review`, { action, demand_lines: action === "approve" ? payloadLines : undefined, remarks: remarks.trim() || null });
        toast.success(action === "approve" ? "Approved — forwarded to the Municipal Admin for final approval" : "Sent back to the Field Collector for correction");
      } else {
        const r = await api.put(`/t/${code}/saf/${saf.id}/admin-review`, { action, demand_lines: action === "approve" ? payloadLines : undefined, remarks: remarks.trim() || null });
        if (action === "approve") toast.success(`Final approval granted — PIN ${r.data.pin} generated and linked to the citizen`);
        else toast.success(action === "reject" ? "Self-assessment rejected — citizen notified" : "Sent back to the Field Collector for correction");
      }
      setConfirm(null);
      onDone?.();
      load();
    } catch (e) { toast.error(e?.response ? errMsg(e) : e.message); } finally { setBusy(false); }
  };

  // Phase 210 — Admin / Super Admin direct decision at any open stage (reason mandatory)
  const actDirect = async () => {
    if (!direct) return;
    const reason = directReason.trim();
    if (reason.length < 5) return toast.error("Please enter the reason (at least 5 characters) — it is recorded in the log and shown to the citizen");
    setBusy(true);
    try {
      const payloadLines = editLines ? linesPayload(lines, defaultFy) : undefined;
      const r = await api.put(`/t/${code}/saf/${saf.id}/direct-review`, { action: direct.action, remarks: reason,
        demand_lines: direct.action === "approve" && payloadLines && payloadLines.length ? payloadLines : undefined });
      if (direct.action === "approve") toast.success(`Approved directly — PIN ${r.data.pin} generated and linked to the citizen`);
      else toast.success("Rejected directly — the citizen has been notified with your reason");
      setDirect(null); setDirectReason("");
      onDone?.();
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (error) return <p className="text-sm text-destructive p-4" data-testid={`${testIdPrefix}-error`}>{error}</p>;
  if (!saf) return <div className="space-y-3 p-2" data-testid={`${testIdPrefix}-loading`}>{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}</div>;

  const rwName = (id) => (masters["road-widths"] || []).find((r) => r.id === id)?.width_label || saf.road_width_label;
  const ptName = (id) => (masters["property-types"] || []).find((p) => p.id === id)?.type_name || saf.property_type_name;
  const sentBackOpen = saf.status === "sent_back";
  const isOpen = !["approved", "rejected"].includes(saf.status);
  const clock = stageDeadlineText(saf);
  const dd = saf.direct_decision || null;
  const withStaff = isOpen && (saf.stage === "collector" || saf.stage === "supervisor");   // case not yet at the Admin stage

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-panel`} data-status={saf.status} data-stage={saf.stage || ""}>
      {/* header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="font-mono text-sm font-semibold" data-testid={`${testIdPrefix}-number`}>{saf.saf_id}</p>
          <p className="text-xs text-muted-foreground">Submitted {fmtDateTime(saf.submitted_at)} by {saf.citizen_name}{saf.citizen_mobile ? ` · ${saf.citizen_mobile}` : ""}</p>
          <SafEscalationTags tags={saf.escalation_tags} className="mt-1.5" testId={`${testIdPrefix}-tags`} size="sm" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SafStatusBadge status={saf.status} testId={`${testIdPrefix}-status`} />
          {saf.pin && <span className="font-mono text-sm font-semibold text-emerald-700" data-testid={`${testIdPrefix}-pin`}>PIN {saf.pin}</span>}
        </div>
      </div>

      {/* Phase 210: stage clock */}
      {clock && (
        <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${clock.overdue ? "border-rose-300 bg-rose-50 text-rose-900" : "border-border bg-muted/30 text-muted-foreground"}`} data-testid={`${testIdPrefix}-clock`} data-overdue={clock.overdue ? "true" : "false"}>
          <TimerReset className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>With the <b>{saf.stage_label}</b> since {fmtDateTime(saf.stage_started_at)} · {clock.text} (deadline {fmtDateTime(saf.stage_deadline_at)}).</span>
        </div>
      )}
      {saf.last_escalation && isOpen && (
        <div className="rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-2 text-xs text-rose-900" data-testid={`${testIdPrefix}-escalation-note`}>
          <b>Auto-escalated</b> {fmtDateTime(saf.last_escalation.at)}: {saf.last_escalation.reason} — moved from {saf.last_escalation.from_label} to {saf.last_escalation.to_label}.
          {saf.escalated_from_collector && !saf.collector_verified_at ? " No physical verification was done — the demand below starts from the citizen's estimate." : ""}
        </div>
      )}
      {saf.routing_note && isOpen && !saf.last_escalation && (
        <p className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-routing-note`}>{saf.routing_note}.</p>
      )}
      {dd && (
        <div className={`rounded-lg border p-3 ${dd.action === "approve" ? "border-indigo-200 bg-indigo-50/60" : "border-red-200 bg-red-50"}`} data-testid={`${testIdPrefix}-direct-decision`}>
          <p className="text-sm font-semibold flex items-center gap-2"><Gavel className="h-4 w-4" /> Direct {dd.action === "approve" ? "approval" : "rejection"} by {dd.by_name} ({dd.by_role_label}) · {fmtDateTime(dd.at)}</p>
          <p className="text-sm mt-1">Reason: “{dd.reason}”</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">Decided while the case was {dd.status_before ? `“${(saf.status_label && dd.status_before === saf.status) ? saf.status_label : dd.status_before.replace(/_/g, " ")}”` : "open"} — recorded in the SAF Approval and Modification Log and shown to the citizen.</p>
        </div>
      )}

      {/* assignment */}
      <div className="flex items-center justify-between gap-2 flex-wrap border rounded-lg px-3 py-2 bg-muted/30" data-testid={`${testIdPrefix}-assignment`}>
        <p className="text-sm flex items-center gap-2">
          <UserRound className="h-4 w-4 text-muted-foreground" />
          {saf.assigned_collector_name ? (<span>Field Collector: <b>{saf.assigned_collector_name}</b>{saf.assigned_collector_employee_id ? ` (${saf.assigned_collector_employee_id})` : ""}
            <span className="text-xs text-muted-foreground"> · {saf.assignment_mode === "auto" ? "auto-assigned by territory" : "assigned manually"}</span></span>)
            : saf.skipped_collector
              ? <span className="text-slate-700">No Field Collector mapped to this ward — physical verification was skipped by rule.</span>
              : <span className="text-amber-800">No Field Collector assigned — no collector covers this ward.</span>}
        </p>
        {can.assign && (
          <Button size="sm" variant="outline" onClick={() => setAssignOpen(true)} data-testid={`${testIdPrefix}-assign-button`}>
            <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" /> {saf.assigned_collector_id ? "Reassign" : "Assign collector"}
          </Button>
        )}
      </div>

      {/* citizen concern */}
      {saf.citizen_remarks ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid={`${testIdPrefix}-concern`}>
          <p className="text-sm font-semibold text-amber-900 flex items-center gap-2"><MessageSquareWarning className="h-4 w-4" /> Citizen's concern about the estimated tax</p>
          <p className="text-sm text-amber-900 mt-1 whitespace-pre-wrap break-words">“{saf.citizen_remarks}”</p>
          <p className="text-[11px] text-amber-800/80 mt-1">Please address this during the physical verification.</p>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid={`${testIdPrefix}-satisfied`}><BadgeCheck className="h-3.5 w-3.5" /> The citizen was satisfied with the estimated tax.</p>
      )}
      {sentBackOpen && saf.sent_back_remarks && (
        <div className="rounded-lg border border-orange-300 bg-orange-50 p-3" data-testid={`${testIdPrefix}-sent-back`}>
          <p className="text-sm font-semibold text-orange-900 flex items-center gap-2"><Undo2 className="h-4 w-4" /> Sent back by {saf.sent_back_by_name} — correction needed</p>
          <p className="text-sm text-orange-900 mt-1">“{saf.sent_back_remarks}”</p>
        </div>
      )}

      {/* property details */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading flex items-center gap-2"><MapPin className="h-4 w-4" /> Property details{editFloors ? " — verify & correct" : ""}</CardTitle></CardHeader>
        <CardContent>
          {editFloors ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[["owner_name", "Owner name *"], ["father_spouse_name", "Father / Spouse"], ["mobile", "Mobile"], ["house_no", "House No"]].map(([k, l]) => (
                <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label><Input value={fields[k]} onChange={(e) => setFields({ ...fields, [k]: e.target.value })} data-testid={`${testIdPrefix}-field-${k}`} /></div>
              ))}
              <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Address</Label><Input value={fields.address} onChange={(e) => setFields({ ...fields, address: e.target.value })} data-testid={`${testIdPrefix}-field-address`} /></div>
              <div className="space-y-1">
                <Label className="text-xs">Road width</Label>
                <Select value={fields.road_width_id || ""} onValueChange={(v) => v && setFields({ ...fields, road_width_id: v })}>
                  <SelectTrigger data-testid={`${testIdPrefix}-field-road`}><SelectValue placeholder="Road width">{rwName(fields.road_width_id) || null}</SelectValue></SelectTrigger>
                  <SelectContent>{(masters["road-widths"] || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.width_label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Property type</Label>
                <Select value={fields.property_type_id || ""} onValueChange={(v) => v && setFields({ ...fields, property_type_id: v })}>
                  <SelectTrigger data-testid={`${testIdPrefix}-field-ptype`}><SelectValue placeholder="Property type">{ptName(fields.property_type_id) || null}</SelectValue></SelectTrigger>
                  <SelectContent>{(masters["property-types"] || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.type_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Field label="Zone › Ward › Mohalla" value={[saf.zone_name, saf.ward_name, saf.mohalla_name].filter(Boolean).join(" › ")} />
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <Field label="Owner" value={saf.owner_name} testId={`${testIdPrefix}-owner`} />
              <Field label="Father / Spouse" value={saf.father_spouse_name} />
              <Field label="Mobile" value={saf.mobile} />
              <Field label="House No" value={saf.house_no} />
              <div className="col-span-2"><Field label="Address" value={saf.address} /></div>
              <Field label="Zone › Ward" value={[saf.zone_name, saf.ward_name].filter(Boolean).join(" › ")} />
              <Field label="Mohalla" value={saf.mohalla_name} />
              <Field label="Road width" value={saf.road_width_label} />
              <Field label="Property type" value={saf.property_type_name} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* floors */}
      {editFloors ? (
        <SafFloorEditor masters={masters} floors={floors} setFloors={setFloors} testIdPrefix={`${testIdPrefix}-floor`} title="Floor-wise assessment — verify on site"
          description="Correct any floor the citizen entered wrongly, add missing floors or remove extra ones. These verified values go on the property record." />
      ) : (
        <SafFloorEditor masters={masters} floors={floorsToEditor(shownFloors)} setFloors={() => {}} readOnly testIdPrefix={`${testIdPrefix}-floor`}
          title={saf.verified_floors ? "Floor-wise assessment (verified by collector)" : "Floor-wise assessment (as declared by citizen)"} description="" />
      )}

      {/* citizen estimate */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading flex items-center gap-2"><Calculator className="h-4 w-4" /> Citizen's estimate</CardTitle></CardHeader>
        <CardContent><SafTaxEstimate estimate={saf.estimate} testId={`${testIdPrefix}-estimate`} /></CardContent>
      </Card>

      {/* final demand */}
      <Card className="shadow-sm border-primary/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading flex items-center justify-between gap-2 flex-wrap">
            <span>Final demand{saf.status === "approved" ? " (approved)" : ""}</span>
            {editLines && (
              <Button type="button" size="sm" variant="outline" onClick={recalculate} disabled={recalc} data-testid={`${testIdPrefix}-recalc`}>
                <Calculator className="h-3.5 w-3.5 mr-1.5" /> {recalc ? "Calculating…" : "Recalculate from rate engine"}
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {editLines ? (
            <SafDemandLinesEditor lines={lines} setLines={setLines} taxHeads={taxHeads} fys={fys} defaultFy={defaultFy} testIdPrefix={`${testIdPrefix}-line`}
              title={isCollector ? "Final demand lines (entered after physical verification)" : "Demand lines (modify if required)"} />
          ) : (
            <SafTaxTable lines={saf.demand_lines && saf.demand_lines.length ? saf.demand_lines : []} testId={`${testIdPrefix}-final-table`} highlight />
          )}
          {saf.collector_remarks && <p className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-collector-remarks`}>Collector's remarks: “{saf.collector_remarks}”</p>}
          {(editLines || editFloors) && !directOnly && (
            <div className="space-y-1">
              <Label className="text-xs">{isCollector ? "Verification remarks" : "Remarks / reason"}{role !== "tax_collector" ? " (required when sending back or rejecting)" : ""}</Label>
              <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid={`${testIdPrefix}-remarks`}
                placeholder={isCollector ? "What you verified on site, corrections made…" : "Why you modified / approved / sent back…"} />
            </div>
          )}
          {directOnly && (
            <p className="text-[11px] text-muted-foreground" data-testid={`${testIdPrefix}-direct-lines-hint`}>
              {saf.demand_lines?.length ? "Demand lines entered so far." : "No staff has entered demand lines yet — these are the citizen's estimated lines."} Modify them if required before a direct approval; the reason is entered in the next step.
            </p>
          )}
          {isCollector && can.verify && (
            <Button className="w-full h-11" onClick={() => act("verify")} disabled={busy} data-testid={`${testIdPrefix}-submit-verify`}>
              <Send className="h-4 w-4 mr-2" /> {busy ? "Submitting…" : "Submit for Supervisor review"}
            </Button>
          )}
          {role === "supervisor" && can.supervisor_review && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button onClick={() => act("approve")} disabled={busy} data-testid={`${testIdPrefix}-sup-approve`}><BadgeCheck className="h-4 w-4 mr-2" /> Approve & forward to Admin</Button>
              <Button variant="outline" className="border-orange-300 text-orange-800 hover:bg-orange-50" onClick={() => act("send_back")} disabled={busy || !saf.assigned_collector_id}
                title={!saf.assigned_collector_id ? "No Field Collector is assigned — the Admin must assign one first" : undefined} data-testid={`${testIdPrefix}-sup-sendback`}>
                <Undo2 className="h-4 w-4 mr-2" /> Send back for correction</Button>
            </div>
          )}
          {isAdmin && can.admin_review && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Button onClick={() => setConfirm({ action: "approve" })} disabled={busy} data-testid={`${testIdPrefix}-admin-approve`}><BadgeCheck className="h-4 w-4 mr-2" /> Final approve & generate PIN</Button>
              <Button variant="outline" className="border-orange-300 text-orange-800 hover:bg-orange-50" onClick={() => act("send_back")} disabled={busy || !saf.assigned_collector_id}
                title={!saf.assigned_collector_id ? "No Field Collector is assigned — use Assign collector first" : undefined} data-testid={`${testIdPrefix}-admin-sendback`}>
                <Undo2 className="h-4 w-4 mr-2" /> Send back</Button>
              <Button variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => act("reject")} disabled={busy} data-testid={`${testIdPrefix}-admin-reject`}>
                <XCircle className="h-4 w-4 mr-2" /> Reject</Button>
            </div>
          )}
          {isAdmin && can.admin_review && saf.status === "collector_verified" && (
            <p className="text-[11px] text-amber-800 flex items-center gap-1.5" data-testid={`${testIdPrefix}-skip-note`}><AlertTriangle className="h-3.5 w-3.5" /> Not yet reviewed by a Supervisor — approving now skips the supervisor step (recorded in the log).</p>
          )}
          {isAdmin && can.admin_review && !saf.assigned_collector_id && (
            <p className="text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid={`${testIdPrefix}-sendback-note`}><AlertTriangle className="h-3.5 w-3.5" /> “Send back” needs a Field Collector — assign one first, or decide the case here.</p>
          )}
        </CardContent>
      </Card>

      {/* Phase 210: direct decision — Admin / Super Admin while the case is still with the Field Collector */}
      {directOnly && (
        <Card className="shadow-sm border-indigo-200 bg-indigo-50/30" data-testid={`${testIdPrefix}-direct-card`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading flex items-center gap-2"><Gavel className="h-4 w-4 text-indigo-700" /> Direct decision — {role === "super_admin" ? "Super Admin" : "Municipal Admin"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              This case is still with the <b>{saf.stage_label}</b>{saf.assigned_collector_name ? ` (${saf.assigned_collector_name})` : ""}. You may decide it now without waiting for the
              physical verification. A reason is <b>mandatory</b> — it is recorded in the SAF Approval &amp; Modification Log and shown to the citizen.
              {clock && !clock.overdue ? ` Otherwise it auto-escalates to the ${clock.to} when the time limit runs out.` : ""}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button onClick={() => { setDirectReason(""); setDirect({ action: "approve" }); }} disabled={busy} data-testid={`${testIdPrefix}-direct-approve`}>
                <BadgeCheck className="h-4 w-4 mr-2" /> Approve directly & generate PIN
              </Button>
              <Button variant="outline" className="border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => { setDirectReason(""); setDirect({ action: "reject" }); }} disabled={busy} data-testid={`${testIdPrefix}-direct-reject`}>
                <XCircle className="h-4 w-4 mr-2" /> Reject directly
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* timeline */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading flex items-center gap-2"><HistoryIcon className="h-4 w-4" /> Approval & modification history</CardTitle></CardHeader>
        <CardContent><SafTimeline history={saf.history || []} testId={`${testIdPrefix}-timeline`} /></CardContent>
      </Card>

      {saf.citizen_mobile && <p className="text-[11px] text-muted-foreground flex items-center gap-1.5"><Phone className="h-3 w-3" /> Citizen contact: {saf.citizen_mobile}</p>}

      <AssignCollectorDialog code={code} saf={saf} open={assignOpen} onClose={() => setAssignOpen(false)} onDone={() => { load(); onDone?.(); }} />

      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent className="max-w-md" data-testid={`${testIdPrefix}-confirm-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-base">Final approval — generate PIN?</DialogTitle>
            <DialogDescription>
              A unique Property PIN will be generated, the property and its demand lines created, and the PIN linked to {saf.citizen_name}'s portal account. The citizen is notified: “Assessment Approved — Your PIN is …”.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirm(null)} data-testid={`${testIdPrefix}-confirm-cancel`}>Cancel</Button>
            <Button onClick={() => act("approve")} disabled={busy} data-testid={`${testIdPrefix}-confirm-approve`}>{busy ? "Approving…" : "Approve & generate PIN"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Phase 210: direct decision dialog — reason is mandatory */}
      <Dialog open={!!direct} onOpenChange={(v) => { if (!v) { setDirect(null); setDirectReason(""); } }}>
        <DialogContent className="max-w-md" data-testid={`${testIdPrefix}-direct-dialog`} data-action={direct?.action || ""}>
          <DialogHeader>
            <DialogTitle className="font-heading text-base flex items-center gap-2">
              <Gavel className="h-4 w-4" /> {direct?.action === "approve" ? "Approve directly — generate PIN?" : "Reject directly?"}
            </DialogTitle>
            <DialogDescription>
              {direct?.action === "approve"
                ? <>The case is still with the {saf.stage_label}. A Property PIN will be generated from the demand lines shown above ({(() => { const t = linesTotal(lines); return fmtINR(t.arrear + t.current); })()}), the property created and the PIN linked to {saf.citizen_name}'s account. The citizen is notified with your reason.</>
                : <>The case is still with the {saf.stage_label}. The citizen is notified of the rejection together with your reason and may submit a corrected self-assessment.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label className="text-xs" htmlFor={`${testIdPrefix}-direct-reason`}>Reason <span className="text-destructive">*</span> <span className="text-muted-foreground font-normal">(recorded in the log, shown to the citizen)</span></Label>
            <Textarea id={`${testIdPrefix}-direct-reason`} rows={3} value={directReason} onChange={(e) => setDirectReason(e.target.value)} autoFocus
              placeholder={direct?.action === "approve" ? "e.g. Documents verified at the counter; collector unavailable this week" : "e.g. Duplicate of an existing property PIN / incorrect ward"}
              aria-invalid={directReason.trim().length > 0 && directReason.trim().length < 5 ? "true" : undefined} data-testid={`${testIdPrefix}-direct-reason`} />
            <p className="text-[11px] text-muted-foreground" data-testid={`${testIdPrefix}-direct-reason-hint`}>At least 5 characters.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setDirect(null); setDirectReason(""); }} data-testid={`${testIdPrefix}-direct-cancel`}>Cancel</Button>
            <Button onClick={actDirect} disabled={busy || directReason.trim().length < 5}
              variant={direct?.action === "reject" ? "destructive" : "default"} data-testid={`${testIdPrefix}-direct-confirm`}>
              {busy ? (direct?.action === "approve" ? "Approving…" : "Rejecting…") : (direct?.action === "approve" ? "Approve & generate PIN" : "Reject self-assessment")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
